// lib/dokumente/blankoBrief.ts
// #539: Blanko-Brief auf dem Briefpapier von Immobilienmanagement Schwede — freier Text,
// Anschriftfeld nach DIN 5008 (Form B), Ausgabe als PDF. Das Briefpapier liegt als Bild
// unter public/briefpapier/ (aus „Immo Schwede Blanko Briefpapier.pdf“, 150 dpi).
import { mitGrussformel } from './grussformel';
import { jsPDF } from 'jspdf';

export const BRIEFPAPIER_URL = '/briefpapier/immo-schwede-a4.jpg';
export const RUECKSENDEZEILE = 'Immobilienmanagement Schwede · Marburger Ring 32a · 35274 Kirchhain';

export interface BlankoBriefDaten {
  empfaenger: string;
  ort: string;
  /** 'YYYY-MM-DD' */
  datum: string;
  betreff: string;
  text: string;
}

/** Maße in mm (A4). Fußzeile des Briefpapiers beginnt bei ~277 mm, Logo endet bei ~31 mm. */
export const LAYOUT = {
  links: 25, rechts: 190, breite: 165,
  ruecksendeY: 49, anschriftY: 56, anschriftZeilen: 6,
  datumY: 98, betreffY: 106,
  folgeseiteStart: 42, ende: 262, zeile: 5.2,
} as const;

/**
 * E-POST (Schablone V3 BK Standard, Seite 1): Absender 45–50,5 mm, DVF-Sperrbereich 52–68 mm (Frankierung der Post –
 * muss leer bleiben), Empfänger 69–90 mm mit höchstens 6 Zeilen 9 pt; alles ab 23 mm von links, 85 mm breit.
 * Werte = Grundlinien in mm von oben.
 */
export const POST_FENSTER = {
  absender: { von: 45, bis: 50.5 }, dvf: { von: 52, bis: 68 }, empfaenger: { von: 69, bis: 90 }, links: 23, breite: 85,
  ruecksendeY: 49, ruecksendeGroesse: 7, anschriftY: 72.2, anschriftZeile: 3.4, anschriftGroesse: 9, anschriftZeilen: 6,
} as const;

/**
 * Die eingebauten PDF-Schriften (Helvetica) kennen nur Latin-1: Gedankenstrich, typografische
 * Anführungszeichen und Co. ließen jsPDF die ganze Zeile gesperrt/ohne Fett setzen.
 */
export function pdfText(s: string, eingebettet = false): string {
  // #589: Ankreuzkästchen – die eingebettete Schrift hat □/■ (nicht ☐/☒), Helvetica gar keine
  const kaestchen = eingebettet
    ? s.replace(/\u2610/g, '\u25A1').replace(/[\u2611\u2612]/g, '\u25A0')
    : s.replace(/[\u2610\u25A1]/g, '[ ]').replace(/[\u2611\u2612\u25A0]/g, '[X]');
  return kaestchen
    .replace(/[\u2013\u2014\u2212]/g, '-')
    .replace(/[\u201E\u201C\u201D\u00AB\u00BB]/g, '"')
    .replace(/[\u201A\u2018\u2019\u2039\u203A]/g, "'")
    .replace(/\u2026/g, '...')
    .replace(/\u20AC/g, 'EUR')
    .replace(/[\u00A0\u2007\u202F]/g, ' ')
    .replace(eingebettet ? /[^\n\x20-\x7E\u00A0-\u00FF\u25A0\u25A1]/g : /[^\n\x20-\x7E\u00A0-\u00FF]/g, '');
}

/** '2026-10-20' → '20.10.2026' (unlesbares bleibt, wie es ist). */
export function datumText(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso.trim();
}

export interface PositionierteZeile { seite: number; y: number; text: string }

/** Zeilen auf Seiten verteilen: Seite 1 ab `start`, Folgeseiten ab `folgeStart`, Umbruch vor `ende`. */
export function verteileZeilen(zeilen: string[], start: number, folgeStart: number, ende: number, zeilenHoehe: number, umbruchVor = -1): PositionierteZeile[] {
  const raus: PositionierteZeile[] = [];
  let seite = 1;
  let y = start;
  for (const [i, text] of zeilen.entries()) {
    if (y > ende || (i === umbruchVor && y !== folgeStart)) { seite++; y = folgeStart; }
    raus.push({ seite, y, text });
    y += zeilenHoehe;
  }
  return raus;
}

/** Erzeugt den Brief als PDF. `hintergrund` = Briefpapier als Data-URL (ohne: weißes Blatt, z. B. im Test). */
/** post=true: Anschrift nach E-POST-Schablone (unterhalb des Frankierbereichs), sonst DIN 5008 wie bisher. */
export interface EingebetteteSchrift { normal: string; fett: string } // TTF als Base64

/** #586 Etappe 2: Briefkopf-Rücksendezeile und Absender mit Unterschrift (Bild als Data-URL, PNG/JPG). */
export interface BriefZusatz {
  ruecksendezeile?: string;
  /** grussformel: ersetzt den Schluss der Vorlage („Mit freundlichen Grüßen …“), leer = Vorlage bleibt */
  /** imAuftrag (#608): „i. A.“ vor die Unterschrift – mit Bild links daneben, ohne Bild vor den Namen */
  unterschrift?: { bild: string | null; name: string; funktion?: string | null; grussformel?: string | null; imAuftrag?: boolean } | null;
}

/** jsPDF-Format aus der Data-URL (Briefpapier/Unterschrift können PNG oder JPG sein). */
export const bildFormat = (dataUrl: string): 'PNG' | 'JPEG' => (/^data:image\/png/i.test(dataUrl) ? 'PNG' : 'JPEG');

/** Platz für den Unterschriftsblock (Höhe in mm) ab y – reicht er nicht bis `ende`, auf die Folgeseite. */
/** #599: Beginn des Schlussblocks (Grußformel), der mit der Unterschrift zusammen bleibt – sonst die letzten zwei Zeilen. */
export function schlussBeginn(zeilen: readonly string[]): number {
  for (let i = zeilen.length - 1; i >= 0; i--) {
    if (/^\s*(mit freundlichen|freundliche gr|viele gr|beste gr|mit besten|herzliche gr)/i.test(zeilen[i])) return i;
  }
  let i = zeilen.length - 1;
  while (i > 0 && !zeilen[i].trim()) i--;
  return Math.max(0, i - 1);
}

/**
 * #599: Zeilen verteilen; passt der Unterschriftsblock (Höhe `block`) nicht mehr hinter die letzte Zeile,
 * wandert die Grußformel mit auf die Folgeseite – die Unterschrift steht nie allein.
 */
export function verteileMitUnterschrift(zeilen: string[], start: number, folgeStart: number, ende: number, zeilenHoehe: number, block: number): PositionierteZeile[] {
  const normal = verteileZeilen(zeilen, start, folgeStart, ende, zeilenHoehe);
  const letzte = normal[normal.length - 1];
  if (!letzte || block <= 0 || letzte.y + 2 + block <= ende) return normal;
  const ab = schlussBeginn(zeilen);
  // Schlussblock steht schon oben auf einer Folgeseite – dann bleibt es beim normalen Umbruch
  if (ab <= 0 || normal[ab].y === folgeStart) return normal;
  return verteileZeilen(zeilen, start, folgeStart, ende, zeilenHoehe, ab);
}

export function unterschriftPlatz(y: number, ende: number, folgeStart: number, hoehe: number): { neueSeite: boolean; y: number } {
  return y + hoehe > ende ? { neueSeite: true, y: folgeStart } : { neueSeite: false, y };
}

// #599: kleiner (vorher 50 × 18 mm) – spart Platz, Unterschrift rutscht seltener auf die Folgeseite
const UNTERSCHRIFT = { breite: 36, hoehe: 12 } as const;
/** #608: Platz für „i. A.“ links neben dem Unterschriftsbild (mm) */
const IM_AUFTRAG_BREITE = 10;
/** Platz der Rücksendezeile: Post-Fenster 23–108 mm, Text beginnt bei 25 mm. */
export const RUECKSENDE_BREITE = 83;

/** post=true: Anschrift nach E-POST-Schablone (unterhalb des Frankierbereichs), sonst DIN 5008 wie bisher.
 *  schrift: fest eingebettete TTF (E-POST/PDF-A verlangt eingebettete Schriften) statt Standard-Helvetica. */
export function erzeugeBlankoBriefPdf(roh: BlankoBriefDaten, hintergrund: string | null, post = false, schrift?: EingebetteteSchrift, zusatz?: BriefZusatz): jsPDF {
  const text = mitGrussformel(roh.text, zusatz?.unterschrift?.grussformel);
  const d: BlankoBriefDaten = { ...roh, empfaenger: pdfText(roh.empfaenger), ort: pdfText(roh.ort), betreff: pdfText(roh.betreff, !!schrift), text: pdfText(text, !!schrift) };
  // putOnlyUsedFonts: sonst stehen die 14 Standardschriften (nicht eingebettet) trotzdem in der Datei
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', putOnlyUsedFonts: true });
  let familie = 'helvetica';
  if (schrift) {
    pdf.addFileToVFS('LiberationSans-Regular.ttf', schrift.normal);
    pdf.addFont('LiberationSans-Regular.ttf', 'LiberationSans', 'normal');
    pdf.addFileToVFS('LiberationSans-Bold.ttf', schrift.fett);
    pdf.addFont('LiberationSans-Bold.ttf', 'LiberationSans', 'bold');
    familie = 'LiberationSans';
  }
  const L = LAYOUT;
  const papier = () => { if (hintergrund) pdf.addImage(hintergrund, bildFormat(hintergrund), 0, 0, 210, 297, 'briefpapier', 'FAST'); };
  papier();
  pdf.setTextColor(30, 30, 30);

  // Rücksendezeile + Anschrift (Fensterkuvert)
  pdf.setFont(familie, 'normal');
  const P = POST_FENSTER;
  pdf.setFontSize(post ? P.ruecksendeGroesse : 7);
  pdf.setTextColor(110, 110, 110);
  // Eigene Rücksendezeilen können länger sein: verkleinern, bis sie ins Absenderfeld der E-POST passt (ab 25 mm, bis 108 mm)
  const ruecksende = pdfText(zusatz?.ruecksendezeile?.trim() || RUECKSENDEZEILE);
  for (let g = post ? P.ruecksendeGroesse : 7; g > 5 && pdf.getTextWidth(ruecksende) > RUECKSENDE_BREITE; g -= 0.25) pdf.setFontSize(g - 0.25);
  pdf.text(ruecksende, L.links, post ? P.ruecksendeY : L.ruecksendeY);
  pdf.setTextColor(30, 30, 30);
  pdf.setFontSize(post ? P.anschriftGroesse : 11);
  d.empfaenger.split('\n').map((z) => z.trim()).filter(Boolean).slice(0, post ? P.anschriftZeilen : L.anschriftZeilen)
    .forEach((z, i) => pdf.text(z, L.links, post ? P.anschriftY + i * P.anschriftZeile : L.anschriftY + i * L.zeile));
  pdf.setFontSize(11);

  // Ort, Datum rechtsbündig
  const ortDatum = [d.ort.trim(), datumText(d.datum)].filter(Boolean).join(', ');
  if (ortDatum) pdf.text(ortDatum, L.rechts, L.datumY, { align: 'right' });

  // Betreff fett
  let y = L.betreffY;
  if (d.betreff.trim()) {
    pdf.setFont(familie, 'bold');
    const betreffZeilen = pdf.splitTextToSize(d.betreff.trim(), L.breite) as string[];
    betreffZeilen.forEach((z, i) => pdf.text(z, L.links, y + i * L.zeile));
    y += betreffZeilen.length * L.zeile + 6;
    pdf.setFont(familie, 'normal');
  }

  // Fließtext — Leerzeilen bleiben erhalten, lange Zeilen werden umbrochen.
  const zeilen = d.text.replace(/\r\n/g, '\n').split('\n')
    .flatMap((absatz) => (absatz.trim() === '' ? [''] : (pdf.splitTextToSize(absatz, L.breite) as string[])));
  let aktuelleSeite = 1;
  const neueSeite = () => { pdf.addPage(); papier(); pdf.setFontSize(11); pdf.setTextColor(30, 30, 30); };
  let letzteY = y - L.zeile;
  const u = zusatz?.unterschrift;
  const funktion = pdfText(u?.funktion?.trim() ?? '');
  const blockHoehe = u?.name.trim() ? (u.bild ? UNTERSCHRIFT.hoehe : 2) + L.zeile * (funktion ? 2 : 1) : 0;
  for (const z of verteileMitUnterschrift(zeilen, y, L.folgeseiteStart, L.ende, L.zeile, blockHoehe)) {
    if (z.seite !== aktuelleSeite) { neueSeite(); aktuelleSeite = z.seite; }
    if (z.text) pdf.text(z.text, L.links, z.y);
    letzteY = z.y;
  }

  // Absender: Unterschrift (Bild), darunter Name und Funktion
  if (u?.name.trim()) {
    const platz = unterschriftPlatz(letzteY + 2, L.ende, L.folgeseiteStart, blockHoehe);
    if (platz.neueSeite) neueSeite();
    let uy = platz.y;
    if (u.bild) {
      const fmt = bildFormat(u.bild);
      const eig = pdf.getImageProperties(u.bild);
      const faktor = Math.min(UNTERSCHRIFT.breite / eig.width, UNTERSCHRIFT.hoehe / eig.height);
      // #608: „i. A.“ links neben der Unterschrift, auf Höhe der Schriftlinie
      const versatz = u.imAuftrag ? IM_AUFTRAG_BREITE : 0;
      if (u.imAuftrag) pdf.text('i. A.', L.links, uy + eig.height * faktor * 0.7);
      pdf.addImage(u.bild, fmt, L.links + versatz, uy, eig.width * faktor, eig.height * faktor, 'unterschrift', 'FAST');
      uy += UNTERSCHRIFT.hoehe;
    }
    uy += 3.5;
    pdf.text(pdfText(`${u.imAuftrag && !u.bild ? 'i. A. ' : ''}${u.name.trim()}`), L.links, uy);
    if (funktion) { pdf.setFontSize(9.5); pdf.setTextColor(90, 90, 90); pdf.text(funktion, L.links, uy + L.zeile * 0.9); pdf.setFontSize(11); pdf.setTextColor(30, 30, 30); }
  }
  // Seitenzahlen ab zwei Seiten
  const anzahl = pdf.getNumberOfPages();
  if (anzahl > 1) {
    pdf.setFontSize(8);
    pdf.setTextColor(110, 110, 110);
    for (let s = 1; s <= anzahl; s++) { pdf.setPage(s); pdf.text(`Seite ${s} von ${anzahl}`, L.rechts, 270, { align: 'right' }); }
  }
  return pdf;
}

/** Briefpapier-Bild als Data-URL laden (für jsPDF). */
export async function ladeBriefpapier(): Promise<string> {
  const res = await fetch(BRIEFPAPIER_URL);
  if (!res.ok) throw new Error('Briefpapier konnte nicht geladen werden');
  const blob = await res.blob();
  return await new Promise<string>((ok, fehler) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result));
    r.onerror = () => fehler(new Error('Briefpapier konnte nicht gelesen werden'));
    r.readAsDataURL(blob);
  });
}

/** Dateiname „2026-10-20 Brief – Betreff.pdf“ ohne problematische Zeichen. */
export function briefDateiname(d: Pick<BlankoBriefDaten, 'datum' | 'betreff'>): string {
  const betreff = d.betreff.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
  return `${d.datum} Brief${betreff ? ` – ${betreff}` : ''}.pdf`;
}

/** Eingebettete Schrift für Post-Briefe (Liberation Sans = Arial-Maße, SIL OFL; public/epost/schrift). */
let schriftCache: Promise<EingebetteteSchrift> | null = null;
export function ladePostSchrift(): Promise<EingebetteteSchrift> {
  const lade = async (datei: string) => {
    const res = await fetch(`/epost/schrift/${datei}`);
    if (!res.ok) throw new Error('Schrift für den Postbrief konnte nicht geladen werden');
    const b = new Uint8Array(await res.arrayBuffer());
    let bin = '';
    for (let i = 0; i < b.length; i += 0x8000) bin += String.fromCharCode(...b.subarray(i, i + 0x8000));
    return btoa(bin);
  };
  schriftCache ??= Promise.all([lade('LiberationSans-Regular.ttf'), lade('LiberationSans-Bold.ttf')])
    .then(([normal, fett]) => ({ normal, fett }))
    .catch((e) => { schriftCache = null; throw e; });
  return schriftCache;
}
